import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@fluentui/react-components';
import { AddFilled, PeopleRegular } from '@fluentui/react-icons';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { StatusLabel } from '@shared/components/StatusLabel';
import { ConfirmDialog, type TConfirmDialogState } from '@shared/components/dialogs/ConfirmDialog';
import { ListPageLayout } from '@shared/components/ListPageLayout';
import { Pagination } from '@shared/components/Pagination';
import { Path } from '@shared/components/Path';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks/useFolderPathAsBreadcrumbs.hook';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { usePageClamp } from '@shared/hooks/usePageClamp.hook';
import toPositiveInteger from '@shared/helpers/toPositiveInteger';
import { calculateRowsPerPage } from '@shared/helpers/calculateRowsPerPage';
import { AccessDialog, type TAccessDialogState, AccessType } from '@modules/access';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import FoldersNavDrawer from '@modules/items/components/nav/FoldersNavDrawer';
import type { IFolder, TPath } from '@modules/items/types/folders.types';
import { useArchiveChangeTemplate, useChangeTemplates, useCreateChangeTemplate } from '../api';
import { getChangeTemplatePath } from '../paths';
import { CHANGE_TEMPLATES_UI_MARGIN_TOP, CHANGE_TEMPLATE_PAGE_SIZE_MAX } from '../constants';
import ChangeTemplateDialog from './ChangeTemplateDialog';
import ChangeTemplatesTable from './ChangeTemplatesTable';
import type { TChangeTemplateListItem } from '../types';

type TProps = {
  qmFolder: IFolder;
  rootFolder: IFolder;
};

const ChangeTemplatesContent = ({ qmFolder, rootFolder }: TProps) => {
  const navigate = useNavigate();
  const { searchParams } = useSearchParams();
  const page = toPositiveInteger(Number(searchParams.get('page')), 1);
  // The server refuses a larger page, which a copied URL can still ask for; Pagination caps what is typed.
  const pageSize = Math.min(
    CHANGE_TEMPLATE_PAGE_SIZE_MAX,
    toPositiveInteger(
      Number(searchParams.get('pageSize')),
      Number(calculateRowsPerPage(CHANGE_TEMPLATES_UI_MARGIN_TOP))
    )
  );
  const { data, error, isLoading } = useChangeTemplates({ page, pageSize });
  usePageClamp(data, isLoading);
  const create = useCreateChangeTemplate();
  const archive = useArchiveChangeTemplate();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState>();
  const { canEdit } = useCanEdit(rootFolder.accessRight);
  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState | undefined>(undefined);
  const canManageAccess = rootFolder.accessRight >= AccessType.MANAGE;
  const fullPath: TPath = [
    { _id: qmFolder._id, name: qmFolder.name, accessRight: qmFolder.accessRight },
    { _id: rootFolder._id, name: TERMINOLOGY.changeTemplate.upperPlural, accessRight: rootFolder.accessRight },
  ];

  const onArchive = (changeTemplate: TChangeTemplateListItem) => {
    const dialogState: TConfirmDialogState = {
      isOpen: true,
      title: `Archive ${changeTemplate.title}?`,
      content: `This ${TERMINOLOGY.changeTemplate.lower} will be moved to the archive.`,
      buttonLabel: 'Archive',
      danger: true,
      onConfirm: async () => {
        try {
          await archive.mutateAsync(changeTemplate._id);
          setConfirmDialogState({ ...dialogState, isOpen: false });
        } catch {
          /* The mutation shows the error; keep the confirmation open for retry. */
        }
      },
    };
    setConfirmDialogState(dialogState);
  };

  return (
    <ListPageLayout nav={<FoldersNavDrawer folder={qmFolder} fullPath={fullPath} selectedValue="change-templates" />}>
      <div className="flex flex-col space-y-5 pt-4 pb-16 pl-5 pr-8 max-w-screen-2xl">
        <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
        {(canEdit || canManageAccess) && (
          <div className="flex gap-2">
            {canEdit && (
              <Button
                appearance="primary"
                size="medium"
                icon={<AddFilled />}
                onClick={() => setIsCreateOpen(true)}
                className="w-fit"
              >
                New
              </Button>
            )}
            {canManageAccess && (
              <Button
                appearance="secondary"
                size="medium"
                icon={<PeopleRegular />}
                onClick={() =>
                  setAccessDialogState({
                    isOpen: true,
                    id: rootFolder._id,
                    nodeType: 'FOLDER',
                    name: TERMINOLOGY.changeTemplate.upperPlural,
                  })
                }
                className="w-fit"
              >
                Access
              </Button>
            )}
          </div>
        )}
        <StatusLabel
          data={data}
          error={error}
          loadingMessage={`Loading ${TERMINOLOGY.changeTemplate.lowerPlural}...`}
          errorMessage={`Loading ${TERMINOLOGY.changeTemplate.lowerPlural} failed!`}
          render={(result) =>
            result.items.length > 0 ? (
              <>
                <ChangeTemplatesTable changeTemplates={result.items} onArchive={onArchive} />
                <Pagination
                  total={result.total}
                  margin={CHANGE_TEMPLATES_UI_MARGIN_TOP}
                  maxPageSize={CHANGE_TEMPLATE_PAGE_SIZE_MAX}
                />
              </>
            ) : (
              <p className="text-neutral-500 py-8">No {TERMINOLOGY.changeTemplate.lowerPlural} found.</p>
            )
          }
        />
        <ChangeTemplateDialog
          isOpen={isCreateOpen}
          setIsOpen={setIsCreateOpen}
          onCreate={async (values) => {
            const id = await create.mutateAsync({ ...values, description: values.description || null });
            navigate(getChangeTemplatePath(id));
          }}
        />
        <AccessDialog state={accessDialogState} setState={setAccessDialogState} />
        <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
      </div>
    </ListPageLayout>
  );
};

export default ChangeTemplatesContent;
